import React, { useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography
} from "@mui/material";
import PropTypes from "prop-types";
import Table from "../../../../components/Table/Table";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { formatDateInLocalTz } from "../../../../utils/datetime";

const eventLogColumns = [
  {
    title: "Title",
    field: "name",
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 260
    }
  },
  {
    title: "Status",
    field: "status",
    options: {
      sorting: true
    },
    cellStyle: {
      width: 160
    }
  },
  {
    title: "Creator",
    field: "creator.id",
    options: {
      sorting: true
    },
    cellStyle: {
      width: 160
    },
    render: rowdata => rowdata.creator && rowdata.creator.name
  },
  {
    title: "Version",
    field: "versionNumber",
    options: {
      sorting: true
    },
    cellStyle: {
      width: 120,
      minWidth: 120
    }
  },
  {
    title: "Uploaded",
    field: "createdDate",
    options: {
      sorting: true
    },
    cellStyle: {
      width: 120,
      minWidth: 120
    },
    render: rowdata => formatDateInLocalTz(rowdata.createdDate)
  },
  {
    title: "internalComments",
    field: "comment",
    options: {
      sorting: true
    },
    cellStyle: {
      width: 200,
      minWidth: 200
    }
  }
];

const useStyles = makeStyles(theme => ({
  icon: {
    color: "white",
    width: theme.spacing(1.5),
    height: theme.spacing(2.25)
  },
  summary: {
    justifyContent: "flex-start"
  },
  summaryContent: {
    flexGrow: 0
  }
}));

function EventLog({ onRowClick, onColumnSort, data, showInternalInformation }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const internalFields = ["comment", "creator.id"];
  const visibleColumns = showInternalInformation
    ? eventLogColumns
    : eventLogColumns.filter(col => !internalFields.includes(col.field));
  if (data && data.length > 0) {
    data.forEach(version => {
      version.versionNumber =
        data.filter(item => parseInt(item.id) < parseInt(version.id)).length +
        1;
    });
  }

  const statusValues = { archived: 0, active: 1, pending: 2 };
  function sortDataByStatus() {
    if (data && data.length > 0 && data[0].status) {
      return data.sort(
        (a, b) => statusValues[a.status] - statusValues[b.status]
      );
    }
  }

  useEffect(() => {
    if (data && data.length > 0) {
      data.forEach(item => {
        if (item.status) {
          item.status = t(
            `manageRepository.manageItemStatuses.${item.status.toLowerCase()}`
          );
        }
      });
    }
  }, [data]);

  return (
    <Box mt={6}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon color="primary" />}
          classes={{ root: classes.summary, content: classes.summaryContent }}
        >
          <Typography variant="h5">
            {t("manageRepository.manageItemEventLog")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table
            columns={visibleColumns}
            filtering={false}
            data={sortDataByStatus()}
            hidePagination={true}
            onSort={onColumnSort}
            onRowClick={(e, rowData) => onRowClick(rowData)}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default EventLog;

EventLog.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      creator: PropTypes.shape({
        name: PropTypes.string
      }),
      version: PropTypes.any,
      uploaded: PropTypes.string,
      comment: PropTypes.string,
      status: PropTypes.string
    })
  ),
  /**
   * Show Creator and Comments columns
   */
  showInternalInformation: PropTypes.bool,
  /**
   * Called when the user sorts one of the columns in the table
   */
  onColumnSort: PropTypes.func,
  onRowClick: PropTypes.func
};
